import { ISeriesApi, LineData, Time, UTCTimestamp } from 'lightweight-charts'
import Chart from './chart'
import { updateCumLineData } from './chartHelpers'
import { CANDLE_THRESHOLD, EXTRA_CANDLES_COUNT } from '../../assets/constants'
import { getCandlesBulkV2 } from '../../services/apis/candles'
import { GetCandlesBulkResponseV2, GetCandlesRequestV2 } from '../../proto/generated/tick-data'
import { convertFromUTC, istToUTC, toDayFormat } from '../../util/dateTime'
import { constructCalendarDataLookup, DataUpdateRequestType, fillCandles, getViewportCandles, mergeCandles, prepareSeriesData } from '../../util/chart'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { Security } from '../../proto/generated/security'
import { exchangeFromJSON } from '../../proto/generated/common'
import { WebSocketResponseMessage } from '../../proto/generated/websocket'
import { Candle } from '../../proto/generated/candle-data-depot-common'
import { CallPutType, MultiStrikeSecuritiesClassType } from '../../reducer/multiStrikeChart.reducer'
import { isOiValueChartType } from './multistrikeChartTypeHelpers'
import { ChartCandle } from '../chartV2/chart.typings'
import { getCandleRequestV2, getTimeWidthInSeconds } from '../chartV2/chartHelpers'
import LineSeries from './series/lineSeries'
import { TradingCalendarData, TradingSession } from '../../proto/generated/trading-calendar'
import { calculateCandlesBetweenDates } from '../../util/chartUtils'

export type ChartDataManagerOptions = {
    chart: Chart
}

class ChartDataManager {
    chart: Chart
    data: Record<string, ChartCandle[]> = {}
    seriesApis: Record<string, LineSeries> = {}
    cumSeriesApis: Record<string, LineSeries | undefined> = {}
    blankSeriesApi: ISeriesApi<'Line', Time> | undefined
    prevOutOfData: Record<string, boolean> = {}
    morningSession: TradingSession | undefined
    nextOutOfData: Record<string, boolean> = {}
    previousTokens: Record<string, string> = {}
    dataLoading = false
    fillEmptySlots = true
    updateSocketData: boolean = false
    forwardDataPending = false
    advanceData: Record<string, Candle[]> = {}
    calendarData: TradingCalendarData | undefined

    constructor(options: ChartDataManagerOptions) {
        this.chart = options.chart
        this.chart.ws.addListener(this.onWebSocketMessageReceived)
        this.calendarData = this.chart.tradingCalendarData
    }

    private updateTradingCalendarInfo = () => {
        if(this.calendarData != this.chart.tradingCalendarData){
            this.calendarData = this.chart.tradingCalendarData
        }
    }

    calculateOiChangeSeries(candle: ChartCandle, previousCandle: ChartCandle | undefined) {
        return previousCandle ? candle.openInterest - previousCandle.openInterest : 0
    }

    recalculateOiChangeSeriesForCandles(candles: ChartCandle[]) {
        if (!candles?.length) return
        for (let i = 0; i < candles.length; i++) {
            candles[i].series = this.calculateOiChangeSeries(candles[i], i > 0 ? candles[i - 1] : undefined)
        }
    }

    recalculateOiChangeSeriesForAllData() {
        if (!isOiValueChartType(this.chart.chartType)) return
        Object.keys(this.data).forEach((key) => {
            this.recalculateOiChangeSeriesForCandles(this.data[key])
        })
    }

    calculateChange(data: LineData<Time>[]): LineData<Time>[] {
        return data.map((current, index, array) => {
            if (index === 0) {
                return { time: current.time, value: 0 }
            }
            const previous = array[index - 1]
            return { time: current.time, value: current.value - previous.value }
        })
    }

    calculateChangeForCandle(
        updatedCandle: LineData<Time>,
        previousCandle: LineData<Time> | null
    ): LineData<Time> | undefined {
        if (!previousCandle && updatedCandle) {
            return { time: updatedCandle.time, value: 0 } // First candle, no change
        }
        if (previousCandle) {
            return {
                time: updatedCandle.time,
                value: updatedCandle.value - previousCandle.value
            }
        }
        return undefined
    }

    fetchData = async ({initial = false, updateConfig = false, newSecuritiesAdded = false, fetchCompulsory = false}) => {
        this.updateTradingCalendarInfo()
        if (this.dataLoading && !initial) {
            return 0
        }
        const presentSecurities = Object.keys(this.data)

        const secondarySecurities = Object.keys(this.chart.secondarySecurities).map(strike => {
            return [this.chart.secondarySecurities[strike].callSecKey, this.chart.secondarySecurities[strike].putSecKey]
        }).flat()
        const securities = !newSecuritiesAdded ? [this.chart.primary, ...secondarySecurities] : [this.chart.primary, ...secondarySecurities].filter(s => !presentSecurities.includes(s))
        const backCandlesRequired: Record<string, number> = {}
        const forwardCandlesRequired: Record<string, number> = {}
        const timeWidth = getTimeWidthInSeconds(this.chart.timeBucket)
        if (initial) {
            this.data = {}
            this.prevOutOfData = {}
            this.nextOutOfData = {}
            this.previousTokens = {}
            this.advanceData = {}
            this.updateSocketData = false
            this.forwardDataPending = false
        }
        let { rangeStart, barsAfter } = this.chart.viewportCandleInfo
        const viewportCandles = getViewportCandles(this.chart.viewportCandleInfo)

        let dateRangeCandleCount = 0
        const selectedDateRange = this.chart.dateRange
        let updateSocketData = false
        if (selectedDateRange) {
            if (initial) {
                const dateRange: [Date, Date] = [new Date(selectedDateRange.startTime), new Date(selectedDateRange.endTime || Date.now())]
                dateRangeCandleCount = Math.ceil(
                    calculateCandlesBetweenDates(dateRange, {
                        timeSeries: this.chart.timeBucket.timeSeries,
                        value: this.chart.timeBucket.timeValue
                    }))
            } else {
                return 0
            }
        }

        const gotoDateCandleCount = Math.ceil(viewportCandles / 2)
        const chartBackwardCandlesNeeded = this.chart.gotoDate ? gotoDateCandleCount : (newSecuritiesAdded ? 0 : (this.data[this.chart.primary]?.length ?? 0)) - (initial ? EXTRA_CANDLES_COUNT : 0) - rangeStart + viewportCandles + CANDLE_THRESHOLD;
        const chartForwardCandlesNeeded = this.chart.gotoDate && initial ? gotoDateCandleCount : viewportCandles + CANDLE_THRESHOLD - barsAfter 

        const backwardCandleRequests: GetCandlesRequestV2[] = []
        const forwardCandleRequests: GetCandlesRequestV2[] = []

        const extraCandleCount = selectedDateRange ? 0 : (initial && !this.chart.gotoDate ? EXTRA_CANDLES_COUNT * 2 : (rangeStart >= 0 ? Math.max(viewportCandles, EXTRA_CANDLES_COUNT) : Math.abs(rangeStart) + EXTRA_CANDLES_COUNT))

        securities.forEach((key) => {
            const sec = {
                exchange: exchangeFromJSON(key.split(':')[0]),
                code: key.split(':')[1]
            } as Security
            const existingData = this.data[key] || []
            const backwardFetchCount = selectedDateRange ? dateRangeCandleCount : chartBackwardCandlesNeeded - existingData.length
            backCandlesRequired[key] = Math.min(backwardFetchCount + extraCandleCount, 500)
            if (initial || existingData.length === 0) {
                backwardCandleRequests.push(getCandleRequestV2(
                    backCandlesRequired[key] > 0 ? backCandlesRequired[key] : extraCandleCount,
                    this.chart.gotoDate ? chartForwardCandlesNeeded + extraCandleCount : 0,
                    sec,
                    this.chart.timeBucket,
                    selectedDateRange ? selectedDateRange.endTime || Date.now() : this.chart.gotoDate ? convertFromUTC(this.chart.gotoDate, this.chart.timezone) * 1000 : 0,
                    false,
                    true
                ))
            } else {
                const candlesRequiredInAddNew = this.data[this.chart.primary]?.length ?? extraCandleCount
                if ((backwardFetchCount > 0 || newSecuritiesAdded) && !this.prevOutOfData[key]) {
                    const lastCandleTime = istToUTC((existingData[0]?.time as number) || 0) * 1000
                    let referenceTime = lastCandleTime + timeWidth * 1000
                    backwardCandleRequests.push(getCandleRequestV2(
                        newSecuritiesAdded ? candlesRequiredInAddNew : backCandlesRequired[key],
                        0,
                        sec,
                        this.chart.timeBucket,
                        referenceTime,
                        false,
                        true
                    ))
                }

                if (chartForwardCandlesNeeded > 0 && !this.nextOutOfData[key]) {
                    const [lastCandle] = existingData.slice(-1)
                    let referenceTime = ((lastCandle?.time as number) || 0) * 1000 + timeWidth
                    if (toDayFormat(referenceTime) !== toDayFormat(Date.now())) {
                        forwardCandlesRequired[key] = Math.min(chartForwardCandlesNeeded, 500)
                        forwardCandleRequests.push(getCandleRequestV2(
                            0,
                            forwardCandlesRequired[key],
                            sec,
                            this.chart.timeBucket,
                            referenceTime
                        ))
                    }
                }

                	
                if ((fetchCompulsory || backwardFetchCount > 0) && !this.prevOutOfData[key]) {
                    let referenceTime = istToUTC((existingData[0]?.time as number) || 0) * 1000
                    backwardCandleRequests.push(getCandleRequestV2(
                        backwardFetchCount + extraCandleCount,
                        0,
                        sec,
                        this.chart.timeBucket,
                        referenceTime
                    ))
                }
                if ((fetchCompulsory || chartForwardCandlesNeeded > 0) && !this.updateSocketData && !this.nextOutOfData[key]) {
                    const [lastCandle] = existingData.slice(-1)
                    const lastCandleTime = istToUTC((lastCandle?.time as number) || 0) * 1000
                    let referenceTime = lastCandleTime + timeWidth * 1000
                    forwardCandlesRequired[key] = Math.min(chartForwardCandlesNeeded, 500)
                    forwardCandleRequests.push(getCandleRequestV2(
                        0,
                        chartForwardCandlesNeeded + extraCandleCount,
                        sec,
                        this.chart.timeBucket,
                        referenceTime,
                        false,
                        true
                    ))
                } 
            }
        })

        const promises: Promise<GetCandlesBulkResponseV2 | null>[] = []
        let requestCount = 0

        if (backwardCandleRequests.length) {
            this.chart.callbacks.onLoading && (initial || newSecuritiesAdded) && this.chart.callbacks.onLoading(true)
            requestCount++
            promises.push(getCandlesBulkV2(
                backwardCandleRequests
            ))
        } else {
            promises.push(Promise.resolve(null))
        }

        if (!forwardCandleRequests.length || initial) {
            promises.push(Promise.resolve(null))
        } else {
            this.chart.callbacks.onLoading && (initial || newSecuritiesAdded) && this.chart.callbacks.onLoading(true)
            requestCount++
            promises.push(getCandlesBulkV2(
                forwardCandleRequests
            ))
        }

        if (!requestCount) {
            return 0
        }

        this.setDataLoading(true)
        const responses = await Promise.all(promises)
        const backwardBulkCandleResponse = responses[0] as GetCandlesBulkResponseV2
        const forwardBulkCandleResponse = responses[1] as GetCandlesBulkResponseV2

        if (backwardCandleRequests.length && backwardBulkCandleResponse) {
            for (const response of backwardBulkCandleResponse.responses) {
                if (!response.security) continue
                const key = getSecurityKey(response.security.exchange, response.security.code)
                const security = await getSecurityAsync(key)
                if (!security) continue
                const existingData = this.data[key] || []
                if (response.backwardCandles.length === 0) {
                    this.prevOutOfData[key] = true
                }
                if (initial || existingData.length === 0) {
                    this.data[key] = prepareSeriesData({candles: response.backwardCandles, priceMultiplier: security.priceMultiplier || 100, timezone: this.chart.timezone, lastTickTimestamp: response.lastCandleStoreUpdateTime, calculateCustomSeries: this.calculateOiChangeSeries, fillEmptySlots: this.fillEmptySlots, timeBucket: this.chart.timeBucket, calendarData: this.calendarData, security})
                } else {
                    const newCandles = prepareSeriesData({candles: response.backwardCandles, priceMultiplier: security.priceMultiplier || 100, timezone: this.chart.timezone, lastTickTimestamp: response.lastCandleStoreUpdateTime, calculateCustomSeries:this.calculateOiChangeSeries, fillEmptySlots: this.fillEmptySlots, timeBucket: this.chart.timeBucket, calendarData: this.calendarData, security})
                    let endIndex = 0
                    while (endIndex < newCandles.length && newCandles[endIndex].time < existingData[0].time) {
                        endIndex++
                    }
                    const newCandlesSlice = newCandles.slice(0, endIndex)
                    if(newCandlesSlice.length && existingData.length){
                        newCandlesSlice.push(...fillCandles({startCandle: newCandlesSlice[newCandlesSlice.length - 1], endCandle: existingData[0], timeBucket: this.chart.timeBucket, calendarData: constructCalendarDataLookup(this.calendarData, security), timezone: this.chart.timezone}))
                    }
                    this.data[key] = [...newCandlesSlice, ...existingData]
                }
                    
                if (response.forwardCandles && response.forwardCandles.length) {
                    const forwardCandles = prepareSeriesData({candles: response.forwardCandles, priceMultiplier: security.priceMultiplier || 100, timezone: this.chart.timezone, lastTickTimestamp: response.lastCandleStoreUpdateTime, calculateCustomSeries: this.calculateOiChangeSeries, fillEmptySlots: this.fillEmptySlots, timeBucket: this.chart.timeBucket, calendarData: this.calendarData, security})
                    const betweenCandles = this.data[key].length ? fillCandles({startCandle: this.data[key][this.data[key].length - 1], endCandle: forwardCandles[0], timeBucket: this.chart.timeBucket, calendarData: constructCalendarDataLookup(this.calendarData, security), timezone: this.chart.timezone}):[]
                    this.data[key] = [...(this.data[key] ?? []), ...betweenCandles, ...forwardCandles]
                }

                if (initial) {
                    this.data[key][this.data[key].length - 1] = {
                        ...this.data[key][this.data[key].length - 1],
                        lastTickTimestamp: response.lastCandleStoreUpdateTime
                    }
                }
            }
        }

        let reachedForwardEnd = 0
        let forwardDataPending = false
        if (forwardCandleRequests.length && forwardBulkCandleResponse) {
            for (let i = 0; i < forwardBulkCandleResponse.responses.length; i++) {
                const response = forwardBulkCandleResponse.responses[i]
                if (!response.security) continue
                const request = forwardCandleRequests[i]
                const key = getSecurityKey(response.security.exchange, response.security.code)
                const security = await getSecurityAsync(key)
                if (!security) continue
                const existingData = this.data[key] || []
                if (response.forwardCandles.length < request.forwardCandleCount) {
                    reachedForwardEnd++
                    this.nextOutOfData[key] = true
                }
                const forwardCandles = prepareSeriesData({candles: response.forwardCandles, priceMultiplier: security.priceMultiplier ?? 100, timezone: this.chart.timezone, lastTickTimestamp: response.lastCandleStoreUpdateTime, calculateCustomSeries: this.calculateOiChangeSeries, fillEmptySlots: this.fillEmptySlots, timeBucket: this.chart.timeBucket, calendarData: this.calendarData, security})
                if(existingData.length && forwardCandles.length){
                    existingData.push(...fillCandles({startCandle: existingData[existingData.length - 1], endCandle: forwardCandles[0], timeBucket: this.chart.timeBucket, calendarData: constructCalendarDataLookup(this.calendarData, security), timezone: this.chart.timezone}))
                }
                this.data[key] = [...existingData, ...forwardCandles]
            }
            if (reachedForwardEnd === forwardCandleRequests.length) {
                updateSocketData = true
            } else {
                forwardDataPending = true
            }
        }
        if (!this.chart.gotoDate && !forwardDataPending && (
            (backwardCandleRequests.length > 0 && forwardCandleRequests.length === 0) || initial
        )) {
            updateSocketData = true
        }
        if (updateSocketData) {
            await this.mergeAdvanceData()
        }
        this.recalculateOiChangeSeriesForAllData()
        if (initial) {
            this.chart.onDataLoaded(DataUpdateRequestType.INITIAL, true, updateConfig)
        } else {
            this.chart.onDataLoaded(DataUpdateRequestType.HISTORICAL, true, updateConfig)
        }
        this.updateSocketData = updateSocketData
        this.forwardDataPending = forwardDataPending
        return backwardCandleRequests.length + forwardCandleRequests.length
    }

    setDataLoading(loading: boolean) {
        this.dataLoading = loading
    }

    removeStrike(strike: string) {
        if (this.chart.secondarySecurities[strike]) {
            const newSecondarySecs = { ...this.chart.secondarySecurities }
            this.removeSecurity([this.chart.secondarySecurities[strike].callSecKey, this.chart.secondarySecurities[strike].putSecKey])
            this.chart.straddleSeries.removeStrike(strike)
            this.chart.oiDiffSeries.removeStrike(strike)

            delete newSecondarySecs[strike]
            this.chart.secondarySecurities = newSecondarySecs
        }
    }

    removeSecurity(secKeys: string[]) {
        this.chart.callbacks.onLoading && this.chart.callbacks.onLoading(true)
        const newDataObj = { ...this.data }
        const seriesDataObj = { ...this.seriesApis }
        secKeys.forEach(secKey => {
            if (seriesDataObj[secKey]) {
                seriesDataObj[secKey].destroy()
            }
            delete newDataObj[secKey]
            delete seriesDataObj[secKey]
            delete this.prevOutOfData[secKey]
            delete this.prevOutOfData[`${secKey}_oi`]
        })
        this.data = newDataObj
        this.seriesApis = seriesDataObj
        this.chart.callbacks.onLoading && this.chart.callbacks.onLoading(false)
    }

    removeAllSecondarySecurities(removePrimary = false) {
        this.chart.callbacks.onLoading && this.chart.callbacks.onLoading(true)
        const newDataObj = { ...this.data }
        const seriesDataObj = { ...this.seriesApis }
        Object.keys(this.chart.secondarySecurities).forEach(strike => {
            const secKeys = [this.chart.secondarySecurities[strike]?.callSecKey, this.chart.secondarySecurities[strike]?.putSecKey]
            secKeys.forEach(secKey => {
                if (seriesDataObj[secKey]) {
                    seriesDataObj[secKey].destroy()
                }
                delete newDataObj[secKey]
                delete seriesDataObj[secKey]
                delete this.prevOutOfData[secKey]
                delete this.prevOutOfData[`${secKey}_oi`]
            })
        })
        if (this.cumSeriesApis['cum_oi_CE']) {
            this.cumSeriesApis['cum_oi_CE'].destroy()
        }
        if (this.cumSeriesApis['cum_oi_PE']) {
            this.cumSeriesApis['cum_oi_PE'].destroy()
        }
        this.chart.straddleSeries.destroyAll()
        this.chart.oiDiffSeries.destroyAll()
        this.chart.secondarySecurities = {}
        if (removePrimary) {
            this.removeSecurity([this.chart.primary])
            this.data = {}
            this.seriesApis = {}
        } else {
            this.data = newDataObj
            this.seriesApis = seriesDataObj
        }
        this.cumSeriesApis = {}
        this.chart.callbacks.onLoading && this.chart.callbacks.onLoading(false)
    }

    addNewSecurities(strike: string, strikeData: { callSecKey: string, putSecKey: string, callPut: CallPutType[] }) {
        const temp = { ...this.chart.secondarySecurities }
        temp[strike] = strikeData
        this.chart.secondarySecurities = temp
        this.fetchData({newSecuritiesAdded: true})
    }

    async addNewStrikes(data: MultiStrikeSecuritiesClassType) {
        if(JSON.stringify(this.chart.secondarySecurities)!==JSON.stringify(data)) {
            if (Object.keys(this.chart.secondarySecurities).length > 0) {
                this.removeAllSecondarySecurities()
            }
            this.chart.secondarySecurities = data
            await this.fetchData({newSecuritiesAdded: true})
        }
    }

    private async applyLiveCandleUpdate(key: string, candle: Candle): Promise<boolean> {
        const candleData = this.data[key]
        if (!candleData) return false
        const [lastCandle]: any = candleData.slice(-1)
        const sec = await getSecurityAsync(key)
        if (!lastCandle || !candle || !sec) return false

        const timeWidth = getTimeWidthInSeconds(this.chart.timeBucket)
        const previousOi = lastCandle.openInterest
        const previousClose = lastCandle.close
        const previousIv = lastCandle.impliedVolatility

        const [updatedCandle] = prepareSeriesData({
            candles: [candle],
            priceMultiplier: sec.priceMultiplier || 100,
            timezone: this.chart.timezone,
            lastTickTimestamp: candle.startTimestamp,
            calculateCustomSeries: this.calculateOiChangeSeries,
            timeBucket: this.chart.timeBucket,
            fillEmptySlots: this.fillEmptySlots,
            calendarData: this.calendarData,
            security: sec,
        })
        const previousTime = lastCandle.time as UTCTimestamp
        const previousLastTickTime = lastCandle.lastTickTimestamp as UTCTimestamp
        const newTime = (previousTime + (Math.floor((Number(updatedCandle.time) - previousTime) / timeWidth) * timeWidth))

        if (Number(updatedCandle.time) >= previousTime + timeWidth) {
            candleData.push(...fillCandles({
                startCandle: lastCandle,
                endCandle: updatedCandle,
                timeBucket: this.chart.timeBucket,
                calendarData: constructCalendarDataLookup(this.calendarData, sec),
                timezone: this.chart.timezone,
            }))
            const newBucketCandle = {
                ...updatedCandle,
                time: newTime as Time,
                lastTickTimestamp: updatedCandle.time as number,
            }
            candleData.push(newBucketCandle)
            this.recalculateOiChangeSeriesForCandles(candleData)
            this.data[key] = [...candleData]
            return true
        }

        if (Number(updatedCandle.time) > previousLastTickTime && Number(updatedCandle.time) < previousTime + timeWidth) {
            const merged = mergeCandles(lastCandle, updatedCandle)
            const oiChanged = merged.openInterest !== previousOi
            const priceChanged = merged.close !== previousClose
            const ivChanged = merged.impliedVolatility !== previousIv
            const isPrimary = key === this.chart.primary
            const isOiChart = isOiValueChartType(this.chart.chartType)
            const dataChanged = isPrimary
                ? priceChanged
                : isOiChart
                  ? oiChanged
                  : this.chart.chartType === 'iv'
                    ? ivChanged
                    : priceChanged
            if (dataChanged) {
                candleData[candleData.length - 1] = merged
                this.recalculateOiChangeSeriesForCandles(candleData)
                this.data[key] = [...candleData]
                return true
            }
        }

        return false
    }

    private async mergeAdvanceData() {
        const changedKeys = new Set<string>()
        for (const [cacheKey, candles] of Object.entries(this.advanceData)) {
            if (!candles.length) continue
            const key = cacheKey.replace(this.chart.subscriptionTag, '')
            for (const candle of candles) {
                if (await this.applyLiveCandleUpdate(key, candle)) {
                    changedKeys.add(key)
                }
            }
            this.advanceData[cacheKey] = []
        }
        if (changedKeys.size > 0) {
            await this.updateCumOiFromChangedKeys(changedKeys)
            this.chart.onDataLoaded(DataUpdateRequestType.LIVE, true, false, false, changedKeys)
        }
    }

    private async updateCumOiFromChangedKeys(changedKeys: Set<string>) {
        const secondaryChanged = [...changedKeys].some((k) => k !== this.chart.primary)
        if (!secondaryChanged || this.chart.chartType !== 'cumOi') return

        const callKeys = Object.keys(this.chart.secondarySecurities).map(s => this.chart.secondarySecurities[s].callSecKey)
        const putKeys = Object.keys(this.chart.secondarySecurities).map(s => this.chart.secondarySecurities[s].putSecKey)
        const callOiDataArray = callKeys.map(k => this.data?.[k] ?? [])
        const putOiDataArray = putKeys.map(k => this.data?.[k] ?? [])

        this.cumSeriesApis['cum_oi_CE']?._setDataAndUpdateCandles(
            updateCumLineData(this.cumSeriesApis['cum_oi_CE'].data as any, callOiDataArray) as ChartCandle[][]
        )
        this.cumSeriesApis['cum_oi_PE']?._setDataAndUpdateCandles(
            updateCumLineData(this.cumSeriesApis['cum_oi_PE'].data as any, putOiDataArray) as ChartCandle[][]
        )
    }

    onWebSocketMessageReceived = async (_data: Uint8Array, message: WebSocketResponseMessage) => {
        const { candleSubscriptionResponses } = message

        if (candleSubscriptionResponses && candleSubscriptionResponses.length > 0) {
            const changedKeys = new Set<string>()
            for (const response of candleSubscriptionResponses) {
                const { securityCandles, tag } = response
                if (tag === this.chart.subscriptionTag) {
                    for (const { candle, security } of securityCandles) {
                        const key = getSecurityKey(security!.exchange, security!.code)
                        if (this.dataLoading || !this.updateSocketData) {
                            if (!candle) continue
                            const cacheKey = key + tag
                            const advanceData = this.advanceData[cacheKey] ?? []
                            if (advanceData.findIndex(d => d.startTimestamp === candle.startTimestamp) === -1) {
                                advanceData.push(candle)
                            }
                            this.advanceData[cacheKey] = [...advanceData]
                            continue
                        }
                        if (candle && await this.applyLiveCandleUpdate(key, candle)) {
                            changedKeys.add(key)
                        }
                    }
                }
            }
            if (changedKeys.size > 0) {
                await this.updateCumOiFromChangedKeys(changedKeys)
                this.chart.onDataLoaded(DataUpdateRequestType.LIVE, true, false, false, changedKeys)
            }
        }
    }

    destroy() {
        this.chart.ws.removeListener(this.onWebSocketMessageReceived)
    }
}

export default ChartDataManager